import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Star, MapPin, Clock, Check, ArrowLeft, Minus, Plus, Loader2, Mountain } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { inr, fmtDate, prettyDate, nextDays } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import Shell from "@/components/Shell";

export default function ExperienceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exp, setExp] = useState(null);
  const [active, setActive] = useState(0);
  const days = nextDays(21);
  const [date, setDate] = useState(fmtDate(days[1]));
  const [guests, setGuests] = useState(2);

  // enquiry form
  const [enq, setEnq] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/experiences/${id}`).then((r) => { setExp(r.data); setActive(0); }).catch(() => navigate("/explore"));
    window.scrollTo(0, 0);
  }, [id, navigate]);

  useEffect(() => {
    if (user) setEnq((e) => ({ ...e, name: e.name || user.name, email: e.email || user.email }));
  }, [user]);

  if (!exp) return <Shell><div className="py-40 grid place-items-center"><Loader2 className="animate-spin text-accent" /></div></Shell>;

  const gallery = exp.gallery?.length ? exp.gallery : [exp.image];

  const proceed = () => {
    navigate("/checkout", { state: {
      type: "experience", item_id: exp.id, title: exp.title, image: exp.image,
      amount: exp.price * guests, currency: "inr",
      details: { date, guests },
      summary: [{ k: "Date", v: prettyDate(date) }, { k: "Guests", v: guests }, { k: "Per person", v: inr(exp.price) }],
    }});
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { experience_id: exp.id, ...enq, dates: date, guests });
      toast.success("Enquiry sent! We'll reach out shortly.");
      setEnq({ name: user?.name || "", email: user?.email || "", phone: "", message: "" });
    } catch (err) { toast.error(apiErr(err)); }
    finally { setSending(false); }
  };

  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <Link to="/explore" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors mb-6"><ArrowLeft size={16} /> Explore</Link>

        {/* Gallery */}
        <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
          <motion.img initial={{ opacity: 0 }} animate={{ opacity: 1 }} key={active} src={gallery[active]} alt={exp.title}
            className="w-full h-72 md:h-[420px] object-cover rounded-2xl border border-primary/10" data-testid="exp-hero-image" />
          <div className="grid grid-cols-3 md:grid-cols-1 gap-3">
            {gallery.slice(0, 3).map((g, i) => (
              <button key={i} onClick={() => setActive(i)} className={`rounded-xl overflow-hidden border-2 h-24 md:h-[135px] ${active === i ? "border-accent" : "border-transparent"}`}>
                <img src={g} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-8 mt-8">
          {/* Left */}
          <div className="md:col-span-7 lg:col-span-8">
            <span className="text-xs font-semibold uppercase tracking-wider text-accent">{exp.category_label}</span>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-foreground mt-2">{exp.title}</h1>
            <div className="flex flex-wrap items-center gap-5 mt-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><MapPin size={15} /> {exp.location}</span>
              <span className="flex items-center gap-1.5"><Clock size={15} /> {exp.duration}</span>
              <span className="flex items-center gap-1.5"><Star size={15} className="fill-accent text-accent" /> {exp.rating}</span>
              {exp.difficulty && <span className="flex items-center gap-1.5"><Mountain size={15} /> {exp.difficulty}</span>}
            </div>
            <p className="text-lg text-foreground/80 leading-relaxed mt-6">{exp.long_description || exp.description}</p>

            {exp.highlights?.length > 0 && (
              <div className="mt-8">
                <h3 className="font-semibold text-foreground mb-3">What's included</h3>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {exp.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-2 text-foreground/80"><span className="grid place-items-center w-5 h-5 rounded-full bg-accent/15 text-accent"><Check size={12} /></span> {h}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right - booking / enquiry */}
          <div className="md:col-span-5 lg:col-span-4">
            <div className="rounded-2xl border border-primary/10 bg-card p-6 sticky top-24" data-testid="exp-booking-box">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-3xl text-foreground">{inr(exp.price)}</span>
                <span className="text-sm text-muted-foreground">per person</span>
              </div>

              {exp.mode === "bookable" ? (
                <>
                  <label className="block mt-5">
                    <span className="text-sm font-medium text-foreground/80 mb-1.5 block">Date</span>
                    <select value={date} onChange={(e) => setDate(e.target.value)} data-testid="exp-date"
                      className="w-full h-11 rounded-lg border border-input bg-transparent px-3 text-sm focus:ring-2 focus:ring-accent focus:outline-none">
                      {days.slice(1).map((d) => <option key={fmtDate(d)} value={fmtDate(d)}>{prettyDate(fmtDate(d))}</option>)}
                    </select>
                  </label>
                  <div className="mt-4">
                    <span className="text-sm font-medium text-foreground/80 mb-1.5 block">Guests</span>
                    <div className="flex items-center justify-between rounded-lg border border-input h-11 px-3">
                      <button onClick={() => setGuests(Math.max(1, guests - 1))} className="text-foreground/70 hover:text-accent" data-testid="guest-minus"><Minus size={16} /></button>
                      <span className="font-semibold" data-testid="guest-count">{guests}</span>
                      <button onClick={() => setGuests(Math.min(exp.max_guests || 12, guests + 1))} className="text-foreground/70 hover:text-accent" data-testid="guest-plus"><Plus size={16} /></button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5 pt-5 border-t border-primary/10">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-display text-2xl text-foreground" data-testid="exp-total">{inr(exp.price * guests)}</span>
                  </div>
                  <button onClick={proceed} data-testid="exp-book-btn"
                    className="w-full mt-4 rounded-full bg-accent text-accent-foreground h-12 font-semibold hover:-translate-y-px transition-transform">
                    Book now
                  </button>
                </>
              ) : (
                <form onSubmit={submitEnquiry} className="mt-5 space-y-3" data-testid="enquiry-form">
                  <p className="text-sm text-muted-foreground">This is a curated trip — tell us a little and our team will craft it for you.</p>
                  <input required value={enq.name} onChange={(e) => setEnq({ ...enq, name: e.target.value })} placeholder="Your name" data-testid="enq-name"
                    className="w-full h-11 rounded-lg border border-input bg-transparent px-3 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
                  <input required type="email" value={enq.email} onChange={(e) => setEnq({ ...enq, email: e.target.value })} placeholder="Email" data-testid="enq-email"
                    className="w-full h-11 rounded-lg border border-input bg-transparent px-3 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
                  <input value={enq.phone} onChange={(e) => setEnq({ ...enq, phone: e.target.value })} placeholder="Phone (optional)" data-testid="enq-phone"
                    className="w-full h-11 rounded-lg border border-input bg-transparent px-3 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
                  <textarea value={enq.message} onChange={(e) => setEnq({ ...enq, message: e.target.value })} rows={3} placeholder="When, how many, any preferences…" data-testid="enq-message"
                    className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
                  <button type="submit" disabled={sending} data-testid="enq-submit"
                    className="w-full rounded-full bg-primary text-primary-foreground h-12 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                    {sending && <Loader2 size={16} className="animate-spin" />} Send enquiry
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}
